// Hero cat. Tries to load the player's series asset from AssetRegistry first;
// if the .glb isn't present, falls back to the voxel placeholder.

import * as THREE from 'three'
import { CatColors, AssetKeys, Field, HungerConfig } from '../config/balance.js'
import { on, Events } from '../ui/eventBus.js'
import { getProfile } from '../game/state.js'
import * as Assets from '../assets/AssetRegistry.js'
import { GROUND_Y } from './hexGrid.js'

// Model-space height of the hero. Field.catScale then shrinks the whole group into
// the world, exactly as it did for the old box cat (~1.5 tall).
const HERO_HEIGHT = 1.5

// Another player's cat for a visited kingdom: the same model, in that player's colour. Not added to any scene, no listeners; the caller calls
// cat.userData.tick?.(dt) each frame (idle animation) and removes it itself.
export function createCatFigure(colorId) {
  const model = Assets.get(AssetKeys.CAT_HERO)
  const cat = model ? buildRigged(model, Assets.getClips(AssetKeys.CAT_HERO)) : buildPlaceholder(colorId)
  cat.position.set(0, GROUND_Y, 0)
  cat.scale.setScalar(Field.catScale)
  recolor(cat, colorId)
  return cat
}

export function createCat({ scene }) {
  const model = Assets.get(AssetKeys.CAT_HERO)
  const cat = model ? buildRigged(model, Assets.getClips(AssetKeys.CAT_HERO)) : buildPlaceholder()
  cat.position.set(0, GROUND_Y, 0)
  cat.scale.setScalar(Field.catScale)
  scene.add(cat)
  applyProfileColor(cat)

  on(Events.ProfileChange, () => applyProfileColor(cat))
  on(Events.CatHungerChange, ({ state }) => applyHungerVisual(cat, state))
  return cat
}

// Hero-specific morph: when the tribe goes hungry, the player's avatar
// doesn't waste away — it BLOATS. Body X/Z scales up to read as a greedy,
// neglectful caretaker; head stays normal so the silhouette is recognizable.
function applyHungerVisual(cat, state) {
  const neglecting = state === 'skinny' || state === 'dying'
  const s = neglecting ? HungerConfig.heroFatBodyXZ : 1
  // Rigged hero: one skinned body, so bloat the whole model sideways.
  const model = cat.userData.model
  if (model) {
    const base = cat.userData.baseScale
    model.scale.set(base * s, base, base * s)
    return
  }
  cat.traverse((obj) => {
    if (obj.userData?.role === 'body') obj.scale.set(s, 1, s)
  })
}

// ── Rigged hero (Mixamo cat: Idle / Run / Jump [/ RumbleDance]) ───────────────
// The model is wrapped in a group so the rest of the game keeps treating `cat` as a
// plain Object3D (position / rotation.y / scale). The wrapper's origin is at the
// feet, and the model faces +Z so heroController's rotation.y = atan2(dx, dz) turns
// it toward the direction of travel.
function buildRigged(model, clips) {
  const cat = new THREE.Group()
  cat.userData.rigged = true

  model.updateMatrixWorld(true)
  const size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3())
  const base = HERO_HEIGHT / (size.y || 1)
  model.scale.setScalar(base)
  model.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(model)
  const center = box.getCenter(new THREE.Vector3())
  model.position.x -= center.x
  model.position.z -= center.z
  model.position.y -= box.min.y // feet on the ground
  cat.add(model)
  cat.userData.model = model
  cat.userData.baseScale = base

  model.traverse((o) => { if (o.isMesh) { o.frustumCulled = false } }) // skinned bounds lag the pose
  attachAnimator(cat, model, clips)
  return cat
}

// Clip name → action. Falls back gracefully if a clip is missing.
function attachAnimator(cat, model, clips) {
  if (!clips.length) return
  const find = (re) => clips.find((c) => re.test(c.name))
  const prepared = (clip) => (clip ? stripRootMotion(clip.clone()) : null)

  const mixer = new THREE.AnimationMixer(model)
  const mk = (clip) => (clip ? mixer.clipAction(clip) : null)
  const idle = mk(prepared(find(/idle/i)) ?? prepared(clips[0]))
  const run = mk(prepared(find(/run|walk/i)))
  const jump = mk(prepared(find(/jump/i)))
  if (jump) { jump.setLoop(THREE.LoopOnce, 1); jump.clampWhenFinished = true }

  let current = null
  const play = (next, fade = 0.2) => {
    if (!next || next === current) return
    next.reset().fadeIn(fade).play()
    if (current) current.fadeOut(fade)
    current = next
  }
  play(idle, 0)

  let jumping = false
  mixer.addEventListener('finished', (e) => { if (e.action === jump) jumping = false })

  // Cosmetic hop (Space) — plays the Jump clip once; no gameplay effect.
  cat.userData.jump = () => {
    if (!jump || jumping) return
    jumping = true
    play(jump, 0.1)
  }

  // Called every frame from main.js (after tickHero has set userData.moving).
  cat.userData.tick = (dt) => {
    if (!jumping) {
      play(cat.userData.moving && run ? run : idle)
      if (run) run.timeScale = cat.userData.sprint ? 1.45 : 1
    }
    mixer.update(dt)
  }
}

// Mixamo clips can bake forward travel into the hips; the game moves the hero itself,
// so pin the hips' X/Z to their first frame and keep the vertical bob.
function stripRootMotion(clip) {
  for (const t of clip.tracks) {
    if (/Hips\.position$/.test(t.name)) {
      const v = t.values
      const x0 = v[0]
      const z0 = v[2]
      for (let i = 0; i < v.length; i += 3) { v[i] = x0; v[i + 2] = z0 }
    }
  }
  return clip
}

function buildPlaceholder(colorId = getProfile().catColorId) {
  const cat = new THREE.Group()
  cat.userData.placeholder = true
  const palette = paletteFor(colorId)
  const bodyMat = new THREE.MeshLambertMaterial({ color: palette.body })
  const headMat = new THREE.MeshLambertMaterial({ color: palette.head })
  const earMat = new THREE.MeshLambertMaterial({ color: palette.ear })

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.8, 0.5), bodyMat)
  body.position.y = 0.45
  body.userData.role = 'body'
  cat.add(body)

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.5, 0.5), headMat)
  head.position.y = 1.1
  head.userData.role = 'head'
  cat.add(head)

  const earGeo = new THREE.BoxGeometry(0.15, 0.18, 0.15)
  const earL = new THREE.Mesh(earGeo, earMat)
  earL.position.set(-0.17, 1.43, 0)
  earL.userData.role = 'ear'
  const earR = new THREE.Mesh(earGeo, earMat)
  earR.position.set(0.17, 1.43, 0)
  earR.userData.role = 'ear'
  cat.add(earL, earR)
  return cat
}

// Recolor either the placeholder (which has explicit body/head/ear roles) or
// — best-effort — a loaded GLB (we walk meshes and tint by mesh name when
// names match `body` / `head` / `ear`). Authors of real assets should name
// their meshes accordingly to get the auto-color behavior; otherwise the GLB
// renders in its baked-in colors.
function applyProfileColor(cat) { recolor(cat, getProfile().catColorId) }
function recolor(cat, colorId) {
  const palette = paletteFor(colorId)
  cat.traverse((obj) => {
    if (!obj.isMesh || !obj.material) return
    const role = obj.userData?.role ?? roleFromName(obj.name)
    const color = palette[role]
    if (color && obj.material.color) obj.material.color.setHex(color)
  })
}

function roleFromName(name) {
  const n = (name ?? '').toLowerCase()
  if (n.includes('body')) return 'body'
  if (n.includes('head')) return 'head'
  if (n.includes('ear')) return 'ear'
  return null
}

function paletteFor(id) {
  return CatColors.find((c) => c.id === id) ?? CatColors[0]
}
